import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSalesReportRequest } from "services/modules/dashboard/index";
import { GetScrollVendorsRequest } from "services/modules/vendors";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme,
  Pagination,
} from "@mui/material";
import BasicDatePicker from "components/Atoms/Input/DatePicker/DatePicker";
import SearchableSelect from "components/Atoms/Input/Select/SearchableSelect";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SaudiRyal from "assets/saudiRyal";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import Breadcrumb from "components/Atoms/SharedComponents/Breadcrumbs/Breadcrumb";

const SalesReport = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { salesReport, isLoading } = useSelector((state) => state.dashboard);
  const { filterVendors, listCount: vendorsCount } = useSelector(
    (state) => state.vendors,
  );

  const isAdmin = localStorage.getItem("user_type") === "AdminUser";

  const [selectedPeriod, setSelectedPeriod] = useState("this_month");
  const [page, setPage] = useState(1);
  const [customDateRange, setCustomDateRange] = useState({
    start_date: dayjs().startOf("month"),
    end_date: dayjs(),
  });
  const [selectedStore, setSelectedStore] = useState(null);

  const periods = [
    {
      id: "custom",
      label: t("dashboard.custom_range"),
      icon: <CalendarTodayIcon fontSize="small" />,
    },
    { id: "past_year", label: t("dashboard.last_year") },
    { id: "this_year", label: t("dashboard.this_year") },
    { id: "this_month", label: t("dashboard.this_month") },
  ];

  const getVendors = (filters) => {
    dispatch(
      GetScrollVendorsRequest({
        params: { ...filters, min_details: true, page_size: 10 },
      }),
    );
  };

  useEffect(() => {
    const params = {
      date_range_type: selectedPeriod,
      page_number: page,
      page_size: 20,
    };
    if (selectedPeriod === "custom") {
      params.start_date = customDateRange.start_date
        ? customDateRange.start_date.format("YYYY-MM-DD")
        : null;
      params.end_date = customDateRange.end_date
        ? customDateRange.end_date.format("YYYY-MM-DD")
        : null;
    }
    if (selectedStore) {
      params.store_id = selectedStore.value;
    }

    dispatch(getSalesReportRequest({ params }));
  }, [dispatch, selectedPeriod, customDateRange, selectedStore, page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const records = salesReport?.data?.sales || [];
  const totalPages =
    salesReport?.extra?.total_pages ??
    salesReport?.meta?.total_pages ??
    Math.ceil((salesReport?.meta?.total_count ?? 1) / 20) ??
    1;

  return (
    <Box sx={{ p: 3 }}>
      <Loader open={isLoading?.loading} />

      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumb
          data={[
            { active: true, path: "/dashboard", title: t("common.home") },
            { active: false, title: t("dashboard.sales_report") },
          ]}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t("dashboard.sales_report")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {periods.map((period) => (
            <Button
              key={period.id}
              variant={selectedPeriod === period.id ? "contained" : "outlined"}
              color="secondary"
              onClick={() => {
                setSelectedPeriod(period.id);
                setPage(1);
              }}
              startIcon={period.icon}
              sx={{
                borderRadius: "12px",
                borderColor: theme.palette.divider,
                height: 44,
                padding: "12px 16px",
                backgroundColor:
                  selectedPeriod === period.id ? "#F3E5F5" : "transparent",
                color:
                  selectedPeriod === period.id
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                border:
                  selectedPeriod === period.id
                    ? `1px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor:
                    selectedPeriod === period.id ? "#E1BEE7" : "#f5f5f5",
                  boxShadow: "none",
                  border:
                    selectedPeriod === period.id
                      ? `1px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                },
              }}
            >
              {period.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Custom Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          justifyContent: "flex-end",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {isAdmin && (
          <SearchableSelect
            options={filterVendors}
            value={selectedStore}
            placeholder={t("financial.vendors")}
            onChange={(val) => {
              setSelectedStore(val);
              setPage(1);
            }}
            onMenuOpen={() => {
              (!filterVendors || filterVendors?.length === 0) &&
                getVendors({ page_number: 1 });
            }}
            getList={getVendors}
            count={vendorsCount}
            applySearchFromRequest={true}
            sx={{
              width: "250px",
              height: "44px",
              "& .react-select__control": {
                height: "44px",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "12px",
              },
            }}
          />
        )}

        {selectedPeriod === "custom" && (
          <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
            <BasicDatePicker
              label={t("dashboard.start_date")}
              value={customDateRange.start_date}
              onChange={(newValue) => {
                setCustomDateRange((prev) => ({
                  ...prev,
                  start_date: newValue,
                }));
                setPage(1);
              }}
              maxDate={customDateRange.end_date}
              filter={false}
            />
            <BasicDatePicker
              label={t("dashboard.end_date")}
              value={customDateRange.end_date}
              onChange={(newValue) => {
                setCustomDateRange((prev) => ({ ...prev, end_date: newValue }));
                setPage(1);
              }}
              minDate={customDateRange.start_date}
              filter={false}
            />
          </Box>
        )}
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "16px",
          boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
          mb: 3,
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="sales report table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("dashboard.order_number")}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                {t("dashboard.order_date")}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                {t("dashboard.order_amount_vat")}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                {t("dashboard.supplier_commission")}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                {t("dashboard.our_commission")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length > 0 ? (
              records.map((row, index) => {
                const totalAmount =
                  row.total_amount_with_vat ??
                  row.total_amount ??
                  row.total_amount_incl_vat ??
                  row.total ??
                  row.order_amount ??
                  0;
                const supplierCommission =
                  row.vendor_commission_amount ??
                  row.supplier_commission ??
                  row.vendor_amount ??
                  row.supplier_amount ??
                  0;
                const ourCommission =
                  row.cadeau_commission_amount ??
                  row.our_commission ??
                  row.cadeau_commission ??
                  row.commission ??
                  row.our_commission_amount ??
                  0;
                const orderDate = row.order_date
                  ? dayjs(row.order_date).format("YYYY-MM-DD")
                  : "—";

                return (
                  <TableRow
                    key={row.order_id ?? row.order_number ?? index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": { backgroundColor: "#fafafa" },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.order_number || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Typography variant="body2">{orderDate}</Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {totalAmount} <SaudiRyal width={16} height={16} />
                      </Box>
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {supplierCommission}{" "}
                        <SaudiRyal width={16} height={16} />
                      </Box>
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {ourCommission} <SaudiRyal width={16} height={16} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  {t("dashboard.no_data")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default SalesReport;
